import {
  TableCheckbox,
  TableCheckboxMultiSelect,
  TableMultiSelect,
  TableSelect,
} from "@/containers/ProjectTemplate";
import { FieldType as EnumFieldType } from "@/enums";
import { IFormFieldOptions } from "@/interfaces";
import { FieldType } from "@/types";
import { Form, Table } from "antd";
import type { FormInstance } from "antd/es/form";
import React, { createContext, useContext, useEffect } from "react";

type EditableTableProps = Parameters<typeof Table>[0];
export type ColumnTypes = Exclude<EditableTableProps["columns"], undefined>;

const EditableContext = createContext<FormInstance | null>(null);

interface IEditableRowProps {
  index: number;
}

export const EditableRow = ({ index, ...props }: IEditableRowProps) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

export interface IEditableCellProps<T> {
  editing: boolean;
  title: React.ReactNode;
  type: FieldType;
  editable: boolean;
  disabled?: boolean;
  required?: boolean;
  options?: IFormFieldOptions;
  children: React.ReactNode;
  showSearch?: boolean;
  dataIndex: string;
  optionsIndex: string;
  placeholder?: string;
  record: T;
  handleSave: (record: T) => void;
}

const allSuffix = "CheckboxSelectedAll";
const noneSuffix = "CheckboxSelectedNone";

export const EditableCell = ({
  title,
  type,
  editable,
  disabled = false,
  required = false,
  options = undefined,
  showSearch = false,
  children,
  dataIndex,
  optionsIndex,
  record,
  handleSave,
  placeholder,
  ...restProps
}: IEditableCellProps<never>) => {
  const form = useContext(EditableContext);

  useEffect(() => {
    if (!editable || !form) return;

    if (record?.[dataIndex] === "") {
      form.resetFields([dataIndex]);
    } else if (record?.[dataIndex]) {
      form.setFieldValue(dataIndex, record[dataIndex]);
    }
  }, [dataIndex, editable, form, record]);

  if (!form) return <></>;

  const save = async (data: unknown): Promise<void> => {
    try {
      form.setFieldsValue({ [dataIndex]: data });
      handleSave({ ...(record as object), [dataIndex]: data } as never);
    } catch (errInfo) {
      console.error("Save failed:", errInfo);
    }
  };

  const saveCheckboxMultiselect = async (
    data: string[],
    all: boolean,
    none: boolean
  ): Promise<void> => {
    try {
      const newRecord = {
        ...(record as object),
        [dataIndex]: data,
        [dataIndex + allSuffix]: all,
        [dataIndex + noneSuffix]: none,
      } as never;
      form.setFieldsValue({ [dataIndex]: data });
      form.setFieldsValue({ [dataIndex + allSuffix]: all });
      form.setFieldsValue({ [dataIndex + noneSuffix]: none });
      handleSave(newRecord);
    } catch (errInfo) {
      console.error("Save failed:", errInfo);
    }
  };

  let childNode = children;
  let hasControl = false;

  if (editable) {
    switch (type) {
      case EnumFieldType.DropDown:
        hasControl = true;
        childNode = (
          <TableSelect
            data-test-id="table-select"
            value={record[dataIndex]}
            options={
              options === undefined
                ? optionsIndex !== "" && record?.[optionsIndex]
                  ? record?.[optionsIndex]
                  : {}
                : options
            }
            showSearch={showSearch}
            onChange={save}
          />
        );
        break;
      case EnumFieldType.MultiSelectDropDown:
        hasControl = true;
        childNode = (
          <TableMultiSelect
            data-test-id="table--multiselect"
            value={record[dataIndex]}
            options={options === undefined ? {} : options}
            onChange={save}
            placeholder={placeholder}
          />
        );
        break;
      case EnumFieldType.CheckboxMultiSelectDropDown:
        hasControl = true;
        childNode = (
          <TableCheckboxMultiSelect
            data-test-id="table-checkbox-multiselect"
            value={record[dataIndex]}
            valueAll={record[dataIndex + allSuffix]}
            valueNone={record[dataIndex + noneSuffix]}
            options={options === undefined ? {} : options}
            onChange={saveCheckboxMultiselect}
          />
        );
        break;
      case EnumFieldType.Checkbox:
        hasControl = true;
        childNode = (
          <TableCheckbox
            data-test-id="table-checkbox"
            checked={record[dataIndex]}
            onChange={save}
            disabled={disabled}
          />
        );
        break;
      default:
        break;
    }

    if (hasControl) {
      return (
        <td {...restProps}>
          <Form.Item
            style={{ margin: 0, width: "100%" }}
            name={dataIndex}
            rules={[
              {
                required: required,
                message: "",
              },
            ]}
            initialValue={record?.[dataIndex]}
          >
            {childNode}
          </Form.Item>
        </td>
      );
    }
  }

  return <td {...restProps}>{childNode}</td>;
};

export const editableComponents = {
  body: {
    row: EditableRow,
    cell: EditableCell,
  },
};
